import cl from 'classnames';
import React, { useMemo, useRef } from 'react';

import { EMPTY_PIN_ENTRY_FIELD, PinEntryField, PinEntryFields, PinIndex, Piwik } from '../types';
import { isValidPinEntryLength, parseNewPinEntryInput } from '../utils/pin-utils';

import '../PinCode.scss';
import './NewPinEntry.scss';

interface NewPinEntryProps {
    newPin: PinEntryFields;
    setNewPin: (pinEntry: PinEntryFields) => void;
    disabled?: boolean;
    hasError?: boolean;
    piwik: Piwik | undefined;
}

interface PinEntryDigitFieldProps {
    disabled: boolean;
    hasError: boolean;
    newPin: PinEntryFields;
    pinIndex: PinIndex;
    refsArray: React.MutableRefObject<HTMLInputElement | null>[];
    setNewPin: (pinEntry: PinEntryFields) => void;
}

const PIN_INDEXES: PinIndex[] = [0, 1, 2, 3];

const PinEntryDigitField = ({
    disabled,
    hasError,
    pinIndex,
    newPin,
    refsArray,
    setNewPin,
}: PinEntryDigitFieldProps) => {
    const pinEntryDigit = newPin[pinIndex];

    const setFocusToPinIndex = (index: PinIndex) => {
        const ref = refsArray[index];

        if (ref.current) {
            ref.current.focus();
            ref.current.select();
        }
    };

    const updatePinIndexFocus = (entryValue: PinEntryField, entryIndex: PinIndex) => {
        // If user has removed value
        if (entryValue === EMPTY_PIN_ENTRY_FIELD) {
            if (entryIndex > 0) {
                setFocusToPinIndex((entryIndex - 1) as PinIndex);
            }
        } else if (entryIndex < 3) {
            setFocusToPinIndex((entryIndex + 1) as PinIndex);
        }
    };

    const updatePinValue = (entryValue: PinEntryField, entryIndex: PinIndex) => {
        const parsedEntryFieldValue: PinEntryField = parseNewPinEntryInput(entryValue);

        const newPinEntryFields = newPin.map((pinEntryField, index) =>
            index === entryIndex ? parsedEntryFieldValue : pinEntryField,
        );

        if (isValidPinEntryLength(newPinEntryFields)) {
            setNewPin(newPinEntryFields);
        }
    };

    const onChangePinEntryDigitField = (entryValue: string | undefined, entryIndex: PinIndex) => {
        const parsedEntryFieldValue: PinEntryField = parseNewPinEntryInput(entryValue);

        updatePinValue(parsedEntryFieldValue, entryIndex);
        updatePinIndexFocus(parsedEntryFieldValue, entryIndex);
    };

    return (
        <div
            className={cl(
                'new-pin-entry__digit-box',
                { 'new-pin-entry__digit-box--disabled': disabled },
                { 'new-pin-entry__digit-box--error': hasError },
            )}
        >
            <input
                value={pinEntryDigit}
                className="new-pin-entry__digit-box__input"
                onChange={({ target }) => {
                    onChangePinEntryDigitField(target.value, pinIndex);
                }}
                maxLength={1}
                minLength={1}
                ref={refsArray[pinIndex]}
                inputMode="numeric"
                disabled={disabled}
                pattern="^[0-1]{1}$"
                onPaste={undefined}
            />
        </div>
    );
};

const NewPinEntry = ({ newPin, setNewPin, disabled = false, hasError = false, piwik }: NewPinEntryProps) => {
    const refField1 = useRef<HTMLInputElement>(null);
    const refField2 = useRef<HTMLInputElement>(null);
    const refField3 = useRef<HTMLInputElement>(null);
    const refField4 = useRef<HTMLInputElement>(null);

    const refsArray = useMemo(() => [refField1, refField2, refField3, refField4], []);

    return (
        <div
            className="pin-code new-pin-entry"
            data-track-id={piwik?.pinCode?.newPinEntry?.dataTrackId}
            data-track-linkname={piwik?.pinCode?.newPinEntry?.dataTrackLinkname}
            id={piwik?.pinCode?.newPinEntry?.dataTrackId}
        >
            <div className="digit-boxes-container">
                {PIN_INDEXES.map((pinIndex) => (
                    <PinEntryDigitField
                        pinIndex={pinIndex}
                        key={pinIndex}
                        refsArray={refsArray}
                        newPin={newPin}
                        setNewPin={setNewPin}
                        disabled={disabled}
                        hasError={hasError}
                    />
                ))}
            </div>
        </div>
    );
};

export default NewPinEntry;
